import React from "react";
import { useQuery } from "@apollo/client/react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { ASSETS_COLLECTION_INVENTORY_MOVE_QUERY } from "src/constants/graphql/queries";
import AssetImage from "src/components/wizard/Item/AssetImage";

const styles = {
  itemContainer: {
    cursor: "pointer",
    borderRadius: 1,
    "&:hover": { bgcolor: "rgba(0,0,0,0.04)" },
  },
  description: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
  },
  locationText: {
    fontSize: 14,
    color: "text.secondary",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
};

const getLocationInfo = rackLocations => {
  if (!rackLocations || rackLocations.length === 0) return { location: null };
  const first = rackLocations[0];
  const rack = first.warehouse_locations?.[0]?.location_name;
  const mobile = first.mobile_warehouse_location?.location_name;
  return { location: rack || mobile || null };
};

export default function AddItemResultsModal({ searchTerm, projectId, onSelect, onBack, onClose }) {
  const { loading, data } = useQuery(ASSETS_COLLECTION_INVENTORY_MOVE_QUERY, {
    variables: {
      search: searchTerm,
      ...(projectId && { projectId }),
      hideDepletedAssets: true,
      order: { item_id: "desc" },
      type: "Asset",
    },
  });

  const assets = data?.assetsCollection?.collection || [];

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton onClick={onBack} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Typography fontSize={22} fontWeight={500} color="textPrimary" flex={1} align="center">
            Select Item to Add
          </Typography>
          <IconButton onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ minHeight: 200, maxHeight: "60vh", overflow: "auto" }}>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" py={6}>
            <CircularProgress />
          </Box>
        ) : assets.length === 0 ? (
          <Box display="flex" flexDirection="column" alignItems="center" py={6} gap={2}>
            <Typography color="textSecondary">No items found matching &ldquo;{searchTerm}&rdquo;</Typography>
            <Button variant="outlined" onClick={onBack}>
              Search Again
            </Button>
          </Box>
        ) : (
          <Box mt={1}>
            {assets.map((asset, index) => {
              const { location } = getLocationInfo(asset.rackLocations);
              const depleted = asset.availableQuantity <= 0;
              return (
                <React.Fragment key={asset.id}>
                  {index > 0 && <Divider />}
                  <Box
                    display="flex"
                    p={1.5}
                    gap={2}
                    sx={{
                      ...styles.itemContainer,
                      ...(depleted && { opacity: 0.5, cursor: "not-allowed" }),
                    }}
                    onClick={depleted ? undefined : () => onSelect(asset)}
                  >
                    <AssetImage width={90} height={80} image={asset?.images?.[0]?.url} />
                    <Box
                      flex={1}
                      display="flex"
                      flexDirection={{ xs: "column", sm: "row" }}
                      gap={{ xs: 0.5, sm: 2 }}
                    >
                      <Box flex={1}>
                        <Typography variant="body1" fontWeight={500}>
                          {asset.itemId}{asset.serialNumber ? ` | ${asset.serialNumber}` : ""}
                        </Typography>
                        <Typography variant="body1" color="textSecondary" gutterBottom sx={styles.description}>
                          {asset.description}
                        </Typography>
                        <Typography variant="body1">
                          Available: {asset.availableQuantity}
                          {depleted && (
                            <Typography component="span" color="error" variant="body2" ml={1}>
                              (Out of stock)
                            </Typography>
                          )}
                        </Typography>
                      </Box>
                      {location && (
                        <Box
                          display="flex"
                          alignItems="center"
                          justifyContent={{ xs: "flex-start", sm: "flex-end" }}
                          minWidth={{ xs: "auto", sm: 120 }}
                        >
                          <Typography sx={styles.locationText}>{location}</Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </React.Fragment>
              );
            })}
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}
